import { captureException } from '@sentry/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Button,
  Col,
  Collapse,
  Divider,
  Form,
  Modal,
  Row,
  Spin,
  Tag,
  Typography
} from 'antd';
import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import Lottie from 'react-lottie';
import { useParams } from 'react-router-dom';
import successAnimation from '../../assets/lotties/success.json';
import { buildPayments } from '../../components/Form/helper';
import ErrorPage from '../../components/globals/ErrorPage';
import TestAlert from '../../components/TestAlert';
import { useAppContext } from '../../context';
import { subscriptionColorByStatus, subscriptionStatusNameByStatus } from '../../enums/enSubscriptionStatus';
import { formatPhone } from '../../helpers/functions';
import { ISubscriptionPaymentMethod } from '../../interfaces/ISubscription';
import { checkoutService } from '../../services/CheckoutService';
import SubscriptionItem from './Item';
import PaymentMethod from './PaymentMethod';

const Subscription = () => {
  const { id } = useParams();

  const { setStyle } = useAppContext();
  const [form] = Form.useForm();
  const [modalError, setModalError] = useState(false);
  const [modalSuccess, setModalSuccess] = useState(false);
  const paymentMethods =
    (Form.useWatch('payments', form) as (ISubscriptionPaymentMethod & {
      isEditing: boolean;
      isNew: boolean;
    })[]) ?? [];

  const { isLoading, data, error, isError, refetch, isFetching } = useQuery({
    queryKey: ['subscription', id],
    queryFn: async () => {
      if (!id) {
        return undefined;
      }

      return checkoutService.mountAsSubscription(id);
    },
    enabled: !!id,
    retry: 0,
  });

  const buildInitialValues = () => {
    if (!data) {
      return {};
    }

    const total = data.items.reduce((acc, item) => acc + item.amount, 0) / 100;

    return {
      payments: data.paymentMethods.map((payment) => ({
        ...payment,
        value: total * (payment.percentageValue / 100),
        isEditing: false,
        isNew: false,
      })),
    };
  };

  useEffect(() => {
    if (!data) {
      return;
    }

    if (data?.styleConfig) {
      setStyle(data.styleConfig);
    }
  }, [setStyle, data]);

  const { mutate, isPending } = useMutation({
    mutationFn: async (body: any) => {
      if (!data) {
        return;
      }

      const payments = await buildPayments(data.teamId, {
        payments: body.payments,
        customer: data.customer,
      });

      const hasInvalidPayment = payments.some((payment) => !payment);

      if (hasInvalidPayment) {
        captureException(new Error('Invalid payment'), { extra: { data, body } });
        setModalError(true);
        return;
      }

      const payload = {
        id: data.id,
        payments,
      };

      return checkoutService.changeSubscriptionPaymentMethods(payload);
    },
    onSuccess: async (result) => {
      if (!result) {
        return;
      }

      if (['changed'].includes(result.status)) {
        setModalSuccess(true);
        return;
      }

      captureException(new Error('Error on change payment method'), { extra: { result, data } });

      setModalError(true);
    },
    onError: (err, variables) => {
      captureException(err, { extra: { data, variables } });
      console.error(err);
      setModalError(true);
    },
  });

  const handleSuccessOk = async () => {
    setModalSuccess(false);
    await refetch();

    //should reset form
    form.setFieldsValue(buildInitialValues());
  };

  const onFinish = useCallback(
    (values: any) => {
      mutate(values);
    },
    [mutate]
  );

  const hasAnyEditingOrNewPayment = paymentMethods.some((payment) => payment.isEditing || payment.isNew);

  if (isLoading || isFetching || !data) {
    return <Spin tip="Carregando..." fullscreen size="large" />;
  }

  if (isError && axios.isAxiosError(error)) {
    const code = error.response?.data?.code;

    if (code == 'NOT_FOUND') {
      return (
        <ErrorPage title="Assinatura não encontrada" renderLogo>
          <p>Geralmente isso ocorre quando o link da assinatura está incorreto.</p>
          <br />
          {data?.companyEmail && data?.companyPhone && (
            <p>Para mais informações entre em contato com <strong className='font-medium text-black'>{data?.companyEmail}</strong> ou no telefone <strong className='font-medium text-black'>{formatPhone(data?.companyPhone)}</strong></p>
          )}
        </ErrorPage>
      )
    }

    return <ErrorPage title="Erro ao carregar a assinatura" renderLogo>Tente novamente mais tarde...</ErrorPage>;
  }

  if (!data?.hasActiveGateways) {
    return (
      <ErrorPage title="Assinatura não disponível" renderLogo>
        <p>Não é possível alterar a forma de pagamento da assinatura.</p>
        <br />
        {data?.companyEmail && data?.companyPhone && (
          <p>Para mais informações entre em contato com <strong className='font-medium text-black'>{data?.companyEmail}</strong> ou no telefone <strong className='font-medium text-black'>{formatPhone(data?.companyPhone)}</strong></p>
        )}
      </ErrorPage>
    );
  }

  return (
    <>
      <Modal
        closable={false}
        open={modalError}
        title="Não foi possível concluir sua compra!"
        footer={[
          <Button type="default" onClick={() => setModalError(false)}>
            Fechar
          </Button>,
        ]}
      >
        <p className="my-3">Abaixo alguns possíveis motivos...</p>

        <Collapse
          collapsible="header"
          defaultActiveKey={['1']}
          ghost
          items={[
            {
              key: '1',
              label: <p className="font-bold">Dados incorretos</p>,
              children: (
                <p>
                  Acontece quando as informações do cartão não foram preenchidas corretamente. Verifique desde o nome,
                  passando pelo número do cartão, data de validade até o código de segurança.
                </p>
              ),
            },
          ]}
        />
        <Collapse
          collapsible="header"
          ghost
          items={[
            {
              key: '2',
              label: <p className="font-bold">Limite insuficiente</p>,
              children: (
                <p>
                  Isso ocorre quando o cartão selecionado não possui limite suficiente para que a compra seja feita.
                  Confira com a operadora do cartão se é possível aumentar o limite ou utilize outra forma de pagamento.
                </p>
              ),
            },
          ]}
        />
        <Collapse
          collapsible="header"
          ghost
          items={[
            {
              key: '3',
              label: <p className="font-bold">Compra suspeita</p>,
              children: (
                <p>
                  Acontece em alguns casos quando a própria operadora do cartão classifica a compra como uma possível
                  fraude. Costuma ocorrer quando uma compra fora do padrão, como por exemplo, uma transação de valor
                  muito alto.
                </p>
              ),
            },
          ]}
        />

        {data?.companyEmail && data?.companyPhone && (
          <p className="mt-5">
            Para mais informações entre em contato com <strong>{data?.companyEmail}</strong> ou no telefone{' '}
            <strong>{formatPhone(data?.companyPhone)}</strong>
          </p>
        )}
      </Modal>

      <Modal
        closable={false}
        open={modalSuccess}
        title="Forma de pagamento alterada com sucesso!"
        footer={[
          <Button className="rounded-md w-20" type="default" size="large" onClick={handleSuccessOk}>
            OK
          </Button>,
        ]}
      >
        <Row justify="center">
          <Col span={24}>
            <Lottie
              options={{
                loop: 0,
                autoplay: true,
                animationData: successAnimation,
                rendererSettings: {
                  preserveAspectRatio: 'xMidYMid slice',
                },
              }}
              width={160}
            />
          </Col>

          <Col span={24} className="messages">
            <Typography.Text className="text-lg text-gray-500 font-bold">
              As próximas cobranças serão feitas com a nova forma de pagamento.
            </Typography.Text>
          </Col>
        </Row>
      </Modal>

      <div className='flex justify-center items-center flex-col p-4'>
        <TestAlert />

        <div className="bg-white rounded-lg border-2 border-gray-200 w-full max-w-2xl p-4 sm:p-8">
          <div className="bg-gray-100 rounded-lg items-center p-4">
            <p className='text-gray-900 text-center font-extrabold'>Alteração da forma de pagamento</p>
            <p className='text-gray-600 text-center font-medium'>Assinatura #{data.code}</p>
          </div>

          <div className='mt-4'>
            <div className='flex items-center gap-2 mb-2'>
              <h4 className='text-gray-500 text-base font-medium'>Detalhes da assinatura</h4>
              <Tag color={subscriptionColorByStatus[data.status]}>{subscriptionStatusNameByStatus[data.status]}</Tag>
            </div>

            {data.items.map((item) => (
              <SubscriptionItem key={item.id} item={item} />
            ))}

            <Divider variant='dashed' />

            <div>
              <div className='mb-2'>
                <h4 className='text-gray-500 text-base font-medium'>
                  Formas de pagamento
                </h4>
              </div>

              <Form
                layout="vertical"
                initialValues={buildInitialValues()}
                onFinish={onFinish}
                scrollToFirstError
                form={form}
              >
                <Form.List name="payments">
                  {(fields) => (
                    <>
                      {fields.map((field) => (
                        <PaymentMethod
                          form={form}
                          key={field.key}
                          name={['payments', field.name]}
                          field={field}
                          subscription={data}
                        />
                      ))}
                    </>
                  )}
                </Form.List>

                <div className='mt-4'>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    disabled={!hasAnyEditingOrNewPayment || isPending}
                    loading={isPending}
                    size="large"
                  >
                    Atualizar Forma de Pagamento
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Subscription;
